import { toDollarsWithCents } from '@/util/currency';
import { FlexProps, Spinner } from '@chakra-ui/react';
import { AnyFinancialEntity, getCurrentValue } from '@common/financialEntities';
import {
    getLastUpdatedBalanceDate,
    isDisconnectedOrError,
} from '@common/react/financialEntities';
import { isNumber } from '@common/util/types';
import { FlexCol } from '../common/Flex';
import { disabledStyleProps } from '../common/styleHelpers';
import { Text, TextProps } from '../common/Typography';
import FinancialEntityRelinkButton from './FinancialEntityRelinkButton';

interface FinancialEntityBalanceProps extends FlexProps {
    fe: AnyFinancialEntity;
    textProps?: TextProps;
    subtitleProps?: TextProps;
    showLastUpdated?: boolean;
    showLoadingIndicator?: boolean;
}

const FinancialEntityBalance = ({
    fe,
    textProps = {},
    subtitleProps = {},
    showLastUpdated,
    showLoadingIndicator = true,
    ...props
}: FinancialEntityBalanceProps) => {
    const balance = getCurrentValue(fe, { useNegativeForDebts: true });
    const lastUpdated = getLastUpdatedBalanceDate(fe).fromNow();
    const isDisconnected =
        fe.type !== 'PlentyGoalAccount' && isDisconnectedOrError(fe);
    const isOpening =
        fe.type === 'PlentyGoalAccount' &&
        !fe.goalAccountOnboarding?.approvedAt;

    if (isOpening) {
        return (
            <FlexCol justifyContent="center" h="full">
                <Text color="textMuted" {...textProps}>
                    Opening
                </Text>
            </FlexCol>
        );
    }

    if (!isNumber(balance) || !lastUpdated) {
        return showLoadingIndicator ? (
            <Spinner speed="1s" color="gray.400" />
        ) : (
            <></>
        );
    }

    return (
        <FlexCol alignItems="flex-end" justify="center" {...props}>
            <Text {...textProps} {...disabledStyleProps(isDisconnected)}>
                {toDollarsWithCents(balance)}
            </Text>
            {isDisconnected && fe.isMine ? (
                <FinancialEntityRelinkButton
                    account={fe}
                    title="Reconnect"
                    size="sm"
                    showArrow={false}
                />
            ) : showLastUpdated ? (
                <Text color="textMuted" whiteSpace="nowrap" {...subtitleProps}>
                    {lastUpdated}
                </Text>
            ) : (
                <></>
            )}
        </FlexCol>
    );
};

export default FinancialEntityBalance;
